// admin-ui/src/marketing/FlipPreviewPanel.tsx
import { useEffect, useState } from "react";
import { Panel } from "../components/Panel";
import { Field } from "../components/Field";
import { Button } from "../components/Button";
import { ApiError } from "../api/client";
import { marketingPicksApi, type CurrentIssue, type PickRow } from "../api/marketing-picks";
import { formFromMeta, formatPreview, MAX_PREVIEW_PAGES, metaKey, parsePreviewPages, previewPageList } from "./preview-pages";

/** Marketing landing → "Flip-viewer preview": which pages of the current
 *  issue the signed-out home page's "Open flip-viewer preview" shows.
 *  One issue_preview pick per issue; the public API uses it only while
 *  that issue is current (public._preview_pick), so a new issue goes
 *  back to pages 1–8 on its own. */
export function FlipPreviewPanel({ pub, currentIssue, picks, onChanged }: {
  pub: string;
  currentIssue: CurrentIssue | null;
  picks: PickRow[];
  onChanged: () => void;
}) {
  const current = currentIssue ? picks.find((p) => p.target_id === currentIssue.id) ?? null : null;
  const others = picks.filter((p) => p !== current);
  const savedMeta = (current?.meta ?? {}) as { spreads?: number[]; single?: number; toc_page?: number };
  const savedSpreads = savedMeta.spreads ?? [];
  const savedSingle = savedMeta.single ?? null;

  const [pages, setPages] = useState("");
  const [toc, setToc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-fill the inputs whenever the saved preview changes — after our
  // own save, a publication switch, or a colleague's edit on refetch —
  // so Save never sends stale text over someone else's change.
  const savedKey = metaKey(current?.meta as Parameters<typeof metaKey>[0]);
  useEffect(() => {
    const form = formFromMeta(current?.meta as Parameters<typeof formFromMeta>[0]);
    setPages(form.pages);
    setToc(form.toc);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, currentIssue?.id, savedKey]);

  if (!currentIssue) {
    return (
      <Panel label="Flip-viewer preview">
        <p className="label">This title has no current issue with pages, so there is no preview.</p>
      </Panel>
    );
  }

  const parsed = parsePreviewPages(pages, currentIssue.page_count);
  const tocNum = toc.trim() ? Number(toc) : null;
  const tocBad = tocNum !== null && (!Number.isInteger(tocNum) || tocNum < 1
    || (currentIssue.page_count !== null && tocNum > currentIssue.page_count));
  const list = parsed.ok ? previewPageList(parsed.spreads, parsed.single) : [];
  const base = (currentIssue.pages_base_url ?? "").replace(/\/$/, "");
  const q = currentIssue.pages_query ? `?${currentIssue.pages_query}` : "";
  const thumb = (n: number) => `${base}/page-${String(n).padStart(3, "0")}.jpg${q}`;

  const fail = (e: unknown) => {
    const detail = e instanceof ApiError && typeof e.detail === "object" && e.detail
      ? ((e.detail as { detail?: string }).detail ?? String(e))
      : String(e);
    setError(detail);
    // Someone else saved a preview for this issue meanwhile: reload the
    // list so the panel shows theirs (and offers Save, not create).
    if (e instanceof ApiError && e.status === 409) onChanged();
  };

  const save = async () => {
    if (!parsed.ok || tocBad) return;
    setBusy(true);
    setError(null);
    const meta: Record<string, unknown> = { spreads: parsed.spreads };
    if (parsed.single !== null) meta.single = parsed.single;
    if (tocNum !== null) meta.toc_page = tocNum;
    try {
      if (current) {
        await marketingPicksApi.patch(current.id, { meta });
      } else {
        await marketingPicksApi.create({
          publication_slug: pub, slot_type: "issue_preview",
          target_kind: "digital_edition", target_id: currentIssue.id, meta,
        });
      }
      onChanged();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };

  const reset = async (pick: PickRow) => {
    setBusy(true);
    setError(null);
    try { await marketingPicksApi.delete(pick.id); onChanged(); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  return (
    <Panel label="Flip-viewer preview">
      <p className="label mb-2">
        The home page's "Open flip-viewer preview" for{" "}
        <span className="text-paper">{currentIssue.title}</span>:{" "}
        {current
          ? <>the cover + pages {formatPreview(savedSpreads, savedSingle)} ({previewPageList(savedSpreads, savedSingle).length} pages).</>
          : <>automatic, pages 1–8.</>}
        {" "}The cover is always first. Choose whole spreads (an even page and the page after it);
        the last item may be one even page, which faces the Keep reading page. Up to{" "}
        {MAX_PREVIEW_PAGES} pages including the cover. A new issue goes back to pages 1–8 until you
        choose pages from it. Changes reach the site within 5 minutes.
      </p>

      <Field
        label="Pages after the cover (e.g. 2–3, 14–17, 20)"
        value={pages}
        onChange={(e) => setPages(e.target.value)}
        placeholder="2–3, 14–17, 20"
      />
      {pages.trim() && !parsed.ok && <p className="text-red-400 text-sm mt-1">{parsed.error}</p>}

      <div className="mt-3 max-w-[240px]">
        <Field
          label="Contents page (optional — the cover's hover side)"
          type="number"
          min={1}
          value={toc}
          onChange={(e) => setToc(e.target.value)}
        />
        {tocBad && <p className="text-red-400 text-sm mt-1">Not a page in this issue.</p>}
      </div>

      {list.length > 0 && base && (
        <div className="mt-3">
          <span className="label mb-1.5 block">What visitors flip through</span>
          <div className="flex flex-wrap gap-3">
            <figure className="w-16">
              <img src={thumb(1)} alt="page 1" className="w-16 aspect-[3/4] object-cover bg-ink-700" />
              <figcaption className="label text-center">1</figcaption>
            </figure>
            {parsed.ok && parsed.spreads.map((left) => (
              <figure key={left} className="w-32">
                <div className="flex gap-px">
                  <img src={thumb(left)} alt={`page ${left}`} className="w-16 aspect-[3/4] object-cover bg-ink-700" />
                  <img src={thumb(left + 1)} alt={`page ${left + 1}`} className="w-16 aspect-[3/4] object-cover bg-ink-700" />
                </div>
                <figcaption className="label text-center">{left}–{left + 1}</figcaption>
              </figure>
            ))}
            <figure className="w-32">
              <div className="flex gap-px">
                {parsed.ok && parsed.single !== null
                  ? <img src={thumb(parsed.single)} alt={`page ${parsed.single}`} className="w-16 aspect-[3/4] object-cover bg-ink-700" />
                  : <div className="w-16" />}
                <div className="w-16 aspect-[3/4] bg-ink-700 border border-ink-500 flex items-center justify-center text-center label px-1">
                  Keep reading
                </div>
              </div>
              <figcaption className="label text-center">
                {parsed.ok && parsed.single !== null ? `${parsed.single} · end` : "end"}
              </figcaption>
            </figure>
          </div>
        </div>
      )}

      {error && <p className="text-red-400 mt-2">{error}</p>}
      <div className="mt-3 flex gap-2">
        <Button onClick={save} disabled={busy || !parsed.ok || tocBad}>
          {current ? "Save preview pages" : "Use these pages"}
        </Button>
        {current && (
          <Button variant="danger" onClick={() => reset(current)} disabled={busy}>
            Back to automatic (pages 1–8)
          </Button>
        )}
      </div>

      {others.length > 0 && (
        <ul className="mt-4 divide-y divide-ink-500">
          {others.map((p) => (
            <li key={p.id} className="py-2 flex items-center justify-between gap-3">
              <span className="label">
                {p.target?.title ?? `issue #${p.target_id}`} ·{" "}
                <span className="text-red-400">⚠ not shown — not the current issue</span>
              </span>
              <Button variant="danger" onClick={() => reset(p)} disabled={busy}>Remove</Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
